import { 
  Shield, 
  Cpu, 
  Globe, 
  BarChart3, 
  Search, 
  Lock, 
  Zap, 
  Activity, 
  Cloud, 
  Network,
  Users,
  Eye,
  Server,
  Terminal,
  ShieldCheck,
  Building2
} from 'lucide-react';
import { LucideIcon } from 'lucide-react';

export type MarketKey = 
  | 'operadoras-isps' 
  | 'operadores-servicos-nuvem' 
  | 'redes-corporativas' 
  | 'ti-corporativa';

export type ScopeKey = 'local' | 'regional' | 'global';

export interface ServiceListItem {
  name: string;
  href: string;
  category: string;
  marketId: MarketKey;
  scope: ScopeKey;
}

export interface MarketStructure {
  id: MarketKey;
  title: string;
  desc: string;
  icon: LucideIcon;
  scopes: Record<ScopeKey, Array<{
    category: string;
    services: Array<{
      name: string;
      href: string;
    }>;
  }>>;
}

export const solutionsStructure: MarketStructure[] = [
  {
    id: 'operadoras-isps',
    title: 'Para operadoras e ISPs',
    desc: 'Proteção, observabilidade e desempenho para redes que sustentam a conectividade.',
    icon: Network,
    scopes: {
      local: [
        {
          category: 'Network Defense',
          services: [
            { name: 'TE - No Appliance Detection', href: '/servicos/no-appliance-detection' },
            { name: 'TE - No Appliance Protection', href: '/servicos/no-appliance-protection' },
            { name: 'TE - Local Scrubbing as a Service - LSaaS', href: '/servicos/local-scrubbing-as-a-service-lsaas' },
            { name: 'TE - Cloud Defense', href: '/servicos/cloud-defense' },
            { name: 'TE - Smart DNS for ISPs', href: '/servicos/smart-dns-isps' }
          ]
        },
        {
          category: 'Professional Services & Integration',
          services: [
            { name: 'TE - Integration and Solution Development', href: '/servicos/integration-solution-development' },
            { name: 'TE - MSSP Services', href: '/servicos/mssp-services' },
            { name: 'TE - SOC Services', href: '/servicos/soc-services' }
          ]
        }
      ],
      regional: [
        {
          category: 'Network Defense',
          services: [
            { name: 'TE - No Appliance Detection', href: '/servicos/no-appliance-detection' },
            { name: 'TE - No Appliance Protection', href: '/servicos/no-appliance-protection' },
            { name: 'TE - Local Scrubbing as a Service - LSaaS', href: '/servicos/local-scrubbing-as-a-service-lsaas' },
            { name: 'TE - Cloud Defense', href: '/servicos/cloud-defense' },
            { name: 'TE - Smart DNS for ISPs', href: '/servicos/smart-dns-isps' }
          ]
        },
        {
          category: 'Next Generation Observability',
          services: [
            { name: 'TE - Network Edge & Core Observability for Providers', href: '/servicos/network-edge-core-observability-providers' },
            { name: 'TE - Advanced Edge & Core Observability for B2C Providers', href: '/servicos/advanced-edge-core-observability-b2c' },
            { name: 'TE - Advanced Edge & Core Observability for Transit Providers', href: '/servicos/advanced-edge-core-observability-transit' },
            { name: 'TE - Managed Traffic Analysis', href: '/servicos/managed-traffic-analysis' },
            { name: 'TE - Full Stack Observability Platform', href: '/servicos/full-stack-observability-platform' },
            { name: 'TE - End-to-End Observability for Providers', href: '/servicos/end-to-end-observability-providers' }
          ]
        },
        {
          category: 'Professional Services & Integration',
          services: [
            { name: 'TE - Integration and Solution Development', href: '/servicos/integration-solution-development' },
            { name: 'TE - MSSP Services', href: '/servicos/mssp-services' },
            { name: 'TE - SOC Services', href: '/servicos/soc-services' }
          ]
        }
      ],
      global: [
        {
          category: 'Network Defense',
          services: [
            { name: 'TE - No Appliance Detection', href: '/servicos/no-appliance-detection' },
            { name: 'TE - No Appliance Protection', href: '/servicos/no-appliance-protection' },
            { name: 'TE - Local Scrubbing as a Service - LSaaS', href: '/servicos/local-scrubbing-as-a-service-lsaas' },
            { name: 'TE - Cloud Defense', href: '/servicos/cloud-defense' },
            { name: 'TE - Smart DNS for ISPs', href: '/servicos/smart-dns-isps' }
          ]
        },
        {
          category: 'Next Generation Observability',
          services: [
            { name: 'TE - Network Edge & Core Observability for Providers', href: '/servicos/network-edge-core-observability-providers' },
            { name: 'TE - Advanced Edge & Core Observability for B2C Providers', href: '/servicos/advanced-edge-core-observability-b2c' },
            { name: 'TE - Advanced Edge & Core Observability for Transit Providers', href: '/servicos/advanced-edge-core-observability-transit' },
            { name: 'TE - Managed Traffic Analysis', href: '/servicos/managed-traffic-analysis' },
            { name: 'TE - Full Stack Observability Platform', href: '/servicos/full-stack-observability-platform' },
            { name: 'TE - End-to-End Observability for Providers', href: '/servicos/end-to-end-observability-providers' }
          ]
        },
        {
          category: 'Professional Services & Integration',
          services: [
            { name: 'TE - Integration and Solution Development', href: '/servicos/integration-solution-development' },
            { name: 'TE - MSSP Services', href: '/servicos/mssp-services' },
            { name: 'TE - SOC Services', href: '/servicos/soc-services' }
          ]
        }
      ]
    }
  },
  {
    id: 'operadores-servicos-nuvem',
    title: 'Para operadores de serviços em nuvem',
    desc: 'Proteção e analytics para ambientes digitais e infraestruturas distribuídas.',
    icon: Cloud,
    scopes: {
      local: [
        {
          category: 'Network Defense',
          services: [
            { name: 'TE - No Appliance Detection', href: '/servicos/no-appliance-detection' },
            { name: 'TE - No Appliance Protection', href: '/servicos/no-appliance-protection' },
            { name: 'TE - Local Scrubbing as a Service - LSaaS', href: '/servicos/local-scrubbing-as-a-service-lsaas' },
            { name: 'TE - Cloud Defense', href: '/servicos/cloud-defense' },
            { name: 'TE - Smart DNS for ISPs', href: '/servicos/smart-dns-isps' }
          ]
        },
        {
          category: 'Cloud Performance Monitoring',
          services: [
            { name: 'TE - Cloud Analytics for Digital Enterprises', href: '/servicos/cloud-analytics-digital-enterprises' }
          ]
        },
        {
          category: 'Next Generation Observability',
          services: [
            { name: 'TE - Hybrid Observability for Digital Enterprises', href: '/servicos/hybrid-observability-digital-enterprises' },
            { name: 'TE - Advanced Edge & Core Observability for B2C Providers', href: '/servicos/advanced-edge-core-observability-b2c' },
            { name: 'TE - Managed Traffic Analysis', href: '/servicos/managed-traffic-analysis' },
            { name: 'TE - Full Stack Observability Platform', href: '/servicos/full-stack-observability-platform' }
          ]
        },
        {
          category: 'Professional Services & Integration',
          services: [
            { name: 'TE - Integration and Solution Development', href: '/servicos/integration-solution-development' },
            { name: 'TE - MSSP Services', href: '/servicos/mssp-services' },
            { name: 'TE - SOC Services', href: '/servicos/soc-services' }
          ]
        }
      ],
      regional: [
        {
          category: 'Network Defense',
          services: [
            { name: 'TE - No Appliance Detection', href: '/servicos/no-appliance-detection' },
            { name: 'TE - No Appliance Protection', href: '/servicos/no-appliance-protection' },
            { name: 'TE - Local Scrubbing as a Service - LSaaS', href: '/servicos/local-scrubbing-as-a-service-lsaas' },
            { name: 'TE - Cloud Defense', href: '/servicos/cloud-defense' },
            { name: 'TE - Smart DNS for ISPs', href: '/servicos/smart-dns-isps' }
          ]
        },
        {
          category: 'Cloud Performance Monitoring',
          services: [
            { name: 'TE - Cloud Analytics for Digital Enterprises', href: '/servicos/cloud-analytics-digital-enterprises' }
          ]
        },
        {
          category: 'Next Generation Observability',
          services: [
            { name: 'TE - Hybrid Observability for Digital Enterprises', href: '/servicos/hybrid-observability-digital-enterprises' },
            { name: 'TE - Advanced Edge & Core Observability for B2C Providers', href: '/servicos/advanced-edge-core-observability-b2c' },
            { name: 'TE - Managed Traffic Analysis', href: '/servicos/managed-traffic-analysis' },
            { name: 'TE - Full Stack Observability Platform', href: '/servicos/full-stack-observability-platform' }
          ]
        },
        {
          category: 'Professional Services & Integration',
          services: [
            { name: 'TE - Integration and Solution Development', href: '/servicos/integration-solution-development' },
            { name: 'TE - MSSP Services', href: '/servicos/mssp-services' },
            { name: 'TE - SOC Services', href: '/servicos/soc-services' }
          ]
        }
      ],
      global: [
        {
          category: 'Network Defense',
          services: [
            { name: 'TE - No Appliance Detection', href: '/servicos/no-appliance-detection' },
            { name: 'TE - No Appliance Protection', href: '/servicos/no-appliance-protection' },
            { name: 'TE - Local Scrubbing as a Service - LSaaS', href: '/servicos/local-scrubbing-as-a-service-lsaas' },
            { name: 'TE - Cloud Defense', href: '/servicos/cloud-defense' },
            { name: 'TE - Smart DNS for ISPs', href: '/servicos/smart-dns-isps' }
          ]
        },
        {
          category: 'Cloud Performance Monitoring',
          services: [
            { name: 'TE - Cloud Analytics for Digital Enterprises', href: '/servicos/cloud-analytics-digital-enterprises' }
          ]
        },
        {
          category: 'Next Generation Observability',
          services: [
            { name: 'TE - Hybrid Observability for Digital Enterprises', href: '/servicos/hybrid-observability-digital-enterprises' },
            { name: 'TE - Advanced Edge & Core Observability for B2C Providers', href: '/servicos/advanced-edge-core-observability-b2c' },
            { name: 'TE - Managed Traffic Analysis', href: '/servicos/managed-traffic-analysis' },
            { name: 'TE - Full Stack Observability Platform', href: '/servicos/full-stack-observability-platform' }
          ]
        },
        {
          category: 'Professional Services & Integration',
          services: [
            { name: 'TE - Integration and Solution Development', href: '/servicos/integration-solution-development' },
            { name: 'TE - MSSP Services', href: '/servicos/mssp-services' },
            { name: 'TE - SOC Services', href: '/servicos/soc-services' }
          ]
        }
      ]
    }
  },
  {
    id: 'redes-corporativas',
    title: 'Para redes corporativas',
    desc: 'Confiabilidade, segurança e operação especializada para a TI de organizações.',
    icon: Building2,
    scopes: {
      local: [
        {
          category: 'Network Defense',
          services: [
            { name: 'TE - Smart DNS for Corporations', href: '/servicos/smart-dns-corporations' }
          ]
        },
        {
          category: 'Professional Services & Integration',
          services: [
            { name: 'TE - Integration and Solution Development', href: '/servicos/integration-solution-development' },
            { name: 'TE - MSSP Services', href: '/servicos/mssp-services' },
            { name: 'TE - SOC Services', href: '/servicos/soc-services' }
          ]
        }
      ],
      regional: [
        {
          category: 'Network Defense',
          services: [
            { name: 'TE - No Appliance Detection', href: '/servicos/no-appliance-detection' },
            { name: 'TE - No Appliance Protection', href: '/servicos/no-appliance-protection' },
            { name: 'TE - Local Scrubbing as a Service - LSaaS', href: '/servicos/local-scrubbing-as-a-service-lsaas' },
            { name: 'TE - Cloud Defense', href: '/servicos/cloud-defense' },
            { name: 'TE - Smart DNS for Corporations', href: '/servicos/smart-dns-corporations' }
          ]
        },
        {
          category: 'Professional Services & Integration',
          services: [
            { name: 'TE - Integration and Solution Development', href: '/servicos/integration-solution-development' },
            { name: 'TE - MSSP Services', href: '/servicos/mssp-services' },
            { name: 'TE - SOC Services', href: '/servicos/soc-services' }
          ]
        }
      ],
      global: [
        {
          category: 'Network Defense',
          services: [
            { name: 'TE - No Appliance Detection', href: '/servicos/no-appliance-detection' },
            { name: 'TE - No Appliance Protection', href: '/servicos/no-appliance-protection' },
            { name: 'TE - Local Scrubbing as a Service - LSaaS', href: '/servicos/local-scrubbing-as-a-service-lsaas' },
            { name: 'TE - Cloud Defense', href: '/servicos/cloud-defense' },
            { name: 'TE - Smart DNS for Corporations', href: '/servicos/smart-dns-corporations' }
          ]
        },
        {
          category: 'Professional Services & Integration',
          services: [
            { name: 'TE - Integration and Solution Development', href: '/servicos/integration-solution-development' },
            { name: 'TE - MSSP Services', href: '/servicos/mssp-services' },
            { name: 'TE - SOC Services', href: '/servicos/soc-services' }
          ]
        }
      ]
    }
  },
  {
    id: 'ti-corporativa',
    title: 'Para TI corporativa',
    desc: 'Eficiência operacional e visibilidade total para equipes de TI e segurança.',
    icon: Users,
    scopes: {
      local: [
        {
          category: 'Professional Services',
          services: [
            { name: 'TE - Integration and Solution Development', href: '/servicos/integration-solution-development' }
          ]
        }
      ],
      regional: [
        {
          category: 'Professional Services',
          services: [
            { name: 'TE - Integration and Solution Development', href: '/servicos/integration-solution-development' }
          ]
        }
      ],
      global: [
        {
          category: 'Professional Services',
          services: [
            { name: 'TE - Integration and Solution Development', href: '/servicos/integration-solution-development' }
          ]
        }
      ]
    }
  }
];

export interface ServiceData {
  title: string;
  slug: string;
  sku: string;
  subtitle: string;
  tagline: string;
  description: string;
  primaryMarket: MarketKey;
  relatedMarkets: MarketKey[];
  technicalCategories: string[];
  targetAudience: {
    idealClient: string;
    useCases: string[];
  };
  benefits: string[]; // Benefícios principais
  competitiveAdvantages: string[]; // Diferenciais competitivos
  uniqueValue: string; // Valor único
  keyFeatures: string[]; // Funcionalidades-chave
  technicalDetails: string[]; // Detalhes técnicos importantes
  commercialModels: string[]; // Modelos de comercialização
  image?: string;
}

export const servicesData: Record<string, ServiceData> = {
  'advanced-edge-core-observability-b2c': {
    title: "TE - Advanced Edge & Core Observability for B2C Providers",
    slug: 'advanced-edge-core-observability-b2c',
    sku: "B2C-TECH-MINDVIEW",
    subtitle: "Aproveite insights de assinantes em tempo real para gerar receita",
    tagline: null,
    description: "Esta solução da TechEnabler foi criada a partir da composição da tecnologia Kentik com a engenharia da TechEnabler, e customizada especificamente para provedores B2C que necessitam de observabilidade avançada de borda e núcleo. Combina análise de tráfego em tempo real com insights de experiência do usuário, permitindo que provedores de serviços otimizem a qualidade da experiência de clientes finais.",
    primaryMarket: 'operadoras-isps',
    relatedMarkets: [],
    technicalCategories: ["Next Generation Observability"],
    targetAudience: {
      idealClient: "provedores B2C, ISPs residenciais, operadoras móveis com foco em clientes finais e provedores de serviços digitais.",
      useCases: ["análise de experiência do usuário individual, otimização de serviços CDN/OTT, gestão de programas zero-rating, planejamento de ofertas personalizadas."]
    },
    benefits: ["visibilidade granular por assinante, insights para retenção, otimização de receita, melhoria da experiência digital."],
    competitiveAdvantages: [],
    uniqueValue: "redução de MTTR (resolução rápida de problemas que afetam a experiência do usuário), aumento do NPS, crescimento de receita com serviços customizados, redução de churn.",
    keyFeatures: ["Subscriber Intelligence, CDN & OTT Analytics, Synthetics Testing."],
    technicalDetails: ["processamento maciço, análise multidimensional granular, retenção de dados forenses, APIs abertas via REST-API. Serviço baseado em plataforma Kentik, SOC TechEnabler e TechEnabler Product Engineering."],
    commercialModels: ["OPEX: SaaS, as a Service."],
    image: "/technabler-solucao-network-edge-&-core-observability-for-providers.webp"
  },
  'advanced-edge-core-observability-transit': {
    title: "TE - Advanced Edge & Core Observability for Transit Providers",
    slug: 'advanced-edge-core-observability-transit',
    sku: "T-TECH-MINDVIEW",
    subtitle: null,
    tagline: null,
    description: "Esta solução da TechEnabler foi desenhada a partir da composição das tecnologias Kentik e Corero com a engenharia da TechEnabler. Foi desenvolvida especificamente para provedores de trânsito IP que necessitam de visibilidade completa e análise avançada de sua infraestrutura de backbone e conexões de trânsito. A solução permite otimizar as operações por meio da plataforma de inteligência de rede com capacidades avançadas de peering e interconexão permitindo construir casos com base em dados de interconexão para redução de custos e aumento de desempenho.",
    primaryMarket: 'operadoras-isps',
    relatedMarkets: [],
    technicalCategories: ["Next Generation Observability"],
    targetAudience: {
      idealClient: "provedores de trânsito, Tier 1 networks, operadores de backbone global, provedores de conectividade internacional.",
      useCases: ["otimização de custos de trânsito, análise de peering e interconexão, inteligência de mercado, capacity planning, monitoramento de SLAs."]
    },
    benefits: ["redução de custos de trânsito, otimização de interconexões, visibilidade global do backbone, inteligência competitiva."],
    competitiveAdvantages: [],
    uniqueValue: "redução de custo, crescimento de receita, IA integrada.",
    keyFeatures: ["Peering Analytics com dados de peering DB integrados, Network Cost Intelligence, BGP monitoring, Synthetics Testing."],
    technicalDetails: ["suporte multiprotocolo NetFlow, sFlow, J-Flow, IPFIX, NetStream, Cflow, retenção forense de dados, alta capacidade, sem limite de quantidade de interfaces. Serviço baseado em plataforma Kentik, SOC TechEnabler e TechEnabler Product Engineering."],
    commercialModels: ["OPEX: SaaS, as a Service."],
    image: "/technabler-solucao-network-edge-&-core-observability-for-providers.webp"
  },
  'cloud-analytics-digital-enterprises': {
    title: "TE - Cloud Analytics for Digital Enterprises",
    slug: 'cloud-analytics-digital-enterprises',
    sku: "TECH-ANALYTICS",
    subtitle: null,
    tagline: null,
    description: "Esta solução da TechEnabler combina a plataforma Kentik com serviços especializados entregando Cloud Performance Analytics e CDN/OTT Analytics, representando uma solução para digital service providers que necessitam de visibilidade de estruturas tanto em nuvem quanto on-premises. Permite o mapeamento da cadeia de suprimentos digitais dos clientes para mais de 350 aplicações OTT e CDNs.",
    primaryMarket: 'operadores-servicos-nuvem',
    relatedMarkets: ["ti-corporativa"],
    technicalCategories: ["Cloud Performance Monitoring"],
    image: "/technabler-solucao-cloud-analytics-for-digital-enterprises.webp",
    targetAudience: {
      idealClient: "digital enterprises que operam em ambientes cloud, datacenters regionais, provedores de cloud locais, empresas digitais.",
      useCases: ["otimização de CDN e OTT, monitoramento de cloud, análise de custos, marketing intelligence."]
    },
    benefits: ["visibilidade unificada de cloud, OTT Analytics avançado, inteligência comercial."],
    competitiveAdvantages: ["escala massiva, IA integrada, expertise local."],
    uniqueValue: "otimização de custos cloud, redução de complexidade.",
    keyFeatures: ["CDN Analytics avançado (mapeia a cadeia de fornecimento em mais de 350 CDNs), cloud monitoring, análise de performance."],
    technicalDetails: ["suporte multi-cloud, analytics sem DPI, análise multidimensional."],
    commercialModels: ["OPEX: SaaS, as a Service."]
  },
  'end-to-end-observability-providers': {
    title: "TE - End-to-End Observability for Providers",
    slug: 'end-to-end-observability-providers',
    sku: "H-TECH-MINDFULL",
    subtitle: null,
    tagline: null,
    description: "Esta solução da TechEnabler combina os sistemas Kentik, Corero e Unryo com serviços especializados TechEnabler entregando end-to-end network slicing e visibilidade e monitoração proativa da experiência do usuário. A solução atua sobre um cenário multi-cloud e híbrido. Especialmente desenvolvida para provedores que enfrentam o adensamento da complexidade de sua rede e o crescimento do nível de exigência de seus usuários.",
    primaryMarket: 'operadoras-isps',
    relatedMarkets: ["operadores-servicos-nuvem"],
    technicalCategories: ["Next Generation Observability"],
    image: "/technabler-solucao-end-to-end-observability-for-providers.webp",
    targetAudience: {
      idealClient: "service providers de grande porte cuja rede esteja em evolução e modernização.",
      useCases: ["otimização operacional, modernização de infraestrutura, monitoramento proativo, análise avançada."]
    },
    benefits: ["observabilidade fim a fim, monitoramento proativo, eficiência operacional, visibilidade estratégica."],
    competitiveAdvantages: ["plataforma única, IA integrada, expertise local, SOCaaS especializado."],
    uniqueValue: "redução de custos, melhoria de SLAs, eficiência da equipe, ROI comprovado.",
    keyFeatures: ["monitoramento multicamada, automação avançada, análise integrada."],
    technicalDetails: ["capacidade massiva, suporte multiprotocolo, escalabilidade ilimitada."],
    commercialModels: ["OPEX: SaaS, as a Service."]
  },
  'full-stack-observability-platform': {
    title: "TE - Full Stack Observability Platform",
    slug: 'full-stack-observability-platform',
    sku: "TECH-MINDFULL",
    subtitle: null,
    tagline: null,
    description: "Esta solução da TechEnabler combina as plataformas Kentik, Corero e Unryo com serviços especializados para entregar Total Intelligence and all Network Telemetry Unified. Utilizando aprendizado de máquina em larga escala, identifica anomalias em tempo real oferecendo serviço dentro do conceito de SDOM (Smart Data Observability per Metrics) e garantindo assim visibilidade unificada de dispositivo cloud e on-premises.",
    primaryMarket: 'operadoras-isps',
    relatedMarkets: ["operadores-servicos-nuvem"],
    technicalCategories: ["Next Generation Observability","Cloud Performance Monitoring"],
    image: "/technabler-solucao-full-stack-observability-platform.webp",
    targetAudience: {
      idealClient: "digital cloud providers, corporate networks e service providers em geral.",
      useCases: ["observabilidade fim a fim, monitoramento multicamada, consolidação de ferramentas e sistemas."]
    },
    benefits: ["visibilidade unificada, IA massiva, consolidação de custos, observabilidade multisserviço."],
    competitiveAdvantages: ["visão multinível e multisserviço unificados."],
    uniqueValue: "eficiência operacional, redução de MTTR, visão estratégica dos sistemas e serviços.",
    keyFeatures: ["enriquecimento de dados, integração de IA, monitoramento multicamada, consolidação geral de dados via IA."],
    technicalDetails: ["suporte multiprotocolo, escalabilidade ilimitada, análise multidimensional, completo conjunto de APIs."],
    commercialModels: ["OPEX: SaaS, as a Service."]
  },
  'hybrid-observability-digital-enterprises': {
    title: "TE - Hybrid Observability for Digital Enterprises",
    slug: 'hybrid-observability-digital-enterprises',
    sku: "H-TECH-MINDVIEW",
    subtitle: null,
    tagline: null,
    description: "Esta solução da TechEnabler combina a plataforma Kentik com serviços especializados para entregar observabilidade completa em ambientes híbridos multi-cloud. Permite visibilidade unificada em redes multi-cloud e híbridas on-premises e datacenters, disponibilizando synthetic tests e traffic flow analysis em um único sistema. A solução visa a atender corporações digitais de todos os portes.",
    primaryMarket: 'operadores-servicos-nuvem',
    relatedMarkets: [],
    technicalCategories: ["Next Generation Observability","Cloud Performance Monitoring"],
    image: "/technabler-solucao-hybrid-observability-for-digital-enterprises.webp",
    targetAudience: {
      idealClient: "digital enterprises, corporate networks, empresas com infraestrutura híbrida multi-cloud.",
      useCases: ["monitoramento híbrido e multi-cloud, otimização de custos de cloud, monitoramento de experiência digital, troubleshooting avançado."]
    },
    benefits: ["visibilidade unificada, redução de custos, eficiência operacional, consolidação de ferramentas e sistemas."],
    competitiveAdvantages: ["insights providos por IA, suporte local."],
    uniqueValue: "redução de MTTR, alta produtividade, visibilidade estratégica.",
    keyFeatures: ["monitoramento híbrido e multi-cloud, monitoramento de desempenho, monitoramento cross-functional (infrastructure, CDNs, multi-cloud e SaaS applications)."],
    technicalDetails: ["multiprotocolo, capacidades de IA, APIs abertas."],
    commercialModels: ["OPEX: SaaS, as a Service."]
  },
  'integration-solution-development': {
    title: "TE - Integration and Solution Development",
    slug: 'integration-solution-development',
    sku: "TECH-SERVICES",
    subtitle: null,
    tagline: null,
    description: "Estes são serviços especializados presentes no portfólio da TechEnabler para oferecer a nossos clientes conforto e tranquilidade na implementação das soluções de proteção e observabilidade de suas redes. A TechEnabler possui completo domínio sobre todos os possíveis cenários de integração das soluções tecnológicas integrantes de seus projetos.",
    primaryMarket: 'operadoras-isps',
    relatedMarkets: ["operadores-servicos-nuvem","redes-corporativas","ti-corporativa"],
    technicalCategories: ["Professional Services & Integration"],
    image: "/technabler-solucao-integration-and-solution-development.webp",
    targetAudience: {
      idealClient: "digital enterprises que operam em ambientes cloud, datacenters regionais, provedores de cloud locais, empresas digitais.",
      useCases: ["integração tecnológica, desenvolvimento customizado, compliance regulatório."]
    },
    benefits: ["expertise consolidada, integração fim a fim, parcerias estratégicas."],
    competitiveAdvantages: ["SOC especializado, customização total, integrações nativas, suporte multilíngue."],
    uniqueValue: "otimização da operação, controle de implantação do projeto, time-to-market.",
    keyFeatures: ["disponibilização de APIs, desenvolvimento customizado, portal multi-tenant, relatórios detalhados."],
    technicalDetails: ["webhooks para notificações, consultas SQL abertas, implementações híbridas."],
    commercialModels: ["OPEX: SaaS, as a Service."]
  },
  'local-scrubbing-as-a-service-lsaas': {
    title: "TE - Local Scrubbing as a Service - LSaaS",
    slug: 'local-scrubbing-as-a-service-lsaas',
    sku: "ISP-TECH-MINDVIEW",
    subtitle: null,
    tagline: null,
    description: "A solução LSaaS é um modelo de negócios desenvolvido pela TechEnabler para tornar mais acessível uma tecnologia premium da defesa contra os ataques DDoS. Montamos, em pontos estratégicos no Brasil, centros de mitigação de alta capacidade. Hoje, estão implementados clusters de mitigação em São Paulo, Rio de Janeiro e Fortaleza; outros estão em análise para serem instalados. Nossos clientes se conectam diretamente a esses centros, enviando seu tráfego e recebendo-o de volta, limpo e livre de ataques. Por ser uma conexão direta, funciona como se ele pertencesse a seu backbone. Na prática, é um novo elemento de rede conectado localmente ao seu roteador. Esse é um modelo de negócios no formato Opex, com pagamentos mensais. O cliente pode começar pequeno, alocando uma fração da capacidade de mitigação disponível, e aumentando essa reserva de acordo com o crescimento da sua rede. Assim não há desperdício de investimento e o cliente paga apenas pelos recursos que usa.",
    primaryMarket: 'operadoras-isps',
    relatedMarkets: [],
    technicalCategories: ["Network Defense"],
    image: "/technabler-solucao-local-scrubbing-as-a-service-lsaas.webp",
    targetAudience: {
      idealClient: "empresas médias que possuem capacidade de links e desejam oferecer uma proteção premium. IT corporations e digital enterprises que não necessitam investir na compra deste equipamento.",
      useCases: ["bloquear ataques DDoS."]
    },
    benefits: ["não introduz latência, proteção Always-On, sem vazamentos, sem quebra de conexões."],
    competitiveAdvantages: ["solução anti-DDoS local premium com ótimo custo-benefício."],
    uniqueValue: "podemos implementar portais de clientes (sub-tenants) para a criação de produtos para geração de receitas.",
    keyFeatures: ["o portal de acesso do cliente fornece todos os dados dos ataques e é atualizado em tempo real. A solução inclui um serviço de SOC para a parametrização das regras de bloqueio e o acompanhamento em caso de ataques."],
    technicalDetails: ["o cliente deve solicitar uma cross-connection no datacenter. O tráfego encaminhado ao centro de scrubbing é decidido pelo cliente."],
    commercialModels: ["OPEX: SaaS, as a Service."]
  },
  'network-edge-core-observability-providers': {
    title: "TE - Network Edge & Core Observability for Providers",
    slug: 'network-edge-core-observability-providers',
    sku: "ISP-TECH-MINDVIEW",
    subtitle: null,
    tagline: null,
    description: "Esta solução da TechEnabler adiciona inteligência aos recursos de observabilidade. Por meio da composição da tecnologia Kentik com a engenharia da TechEnabler, é possível oferecer um sistema de observabilidade e inteligência artificial com base nos desafios enfrentados por ISPs. Assim, temos uma plataforma de inteligência de rede especificamente dedicada a equipes de infraestrutura modernas, permitindo visibilidade completa de redes híbridas e multi-cloud. A solução unifica e correlaciona dados em nuvem, dispositivos, fluxos e dados sintéticos em uma única plataforma.",
    primaryMarket: 'operadoras-isps',
    relatedMarkets: [],
    technicalCategories: ["Next Generation Observability"],
    image: "/technabler-solucao-network-edge-&-core-observability-for-providers.webp",
    targetAudience: {
      idealClient: "provedores (ISPs), operadoras de telecomunicações, empresas Tier 1 e Tier 2.",
      useCases: ["análise de tráfego de rede, planejamento de capacidade, otimização de peering e interconexões, análise de custos de conectividade e trânsito, monitoramento proativo de rede, detecção e mitigação de DDoS."]
    },
    benefits: ["velocidade de implementação superior à das ferramentas tradicionais, escalabilidade massiva com volume ilimitado de flows, interface intuitiva com módulo de IA, suporte local por meio do SOC TechEnabler."],
    competitiveAdvantages: ["100% em nuvem, aceleração do trabalho com IA integrada."],
    uniqueValue: "redução de custos de conectividade, visibilidade 360 graus para decisões proativas. Economia de tempo no diagnóstico e combate, antecipação de falhas, geração de receitas via portal multi-tenant TechEnabler.",
    keyFeatures: ["compatível com NetFlow, sFlow, IPFIX, SNMP, e BGP; enriquecimento de dados com geolocalização, peering DB e dados comerciais, machine learning, APIs abertas, dashboards personalizados."],
    technicalDetails: ["serviço baseado em plataforma Kentik e SOC TechEnabler."],
    commercialModels: ["OPEX: SaaS, as a Service."]
  },
  'managed-traffic-analysis': {
    title: "TE - Managed Traffic Analysis",
    slug: 'managed-traffic-analysis',
    sku: "TECH-TRAFFIC MANAGER",
    subtitle: null,
    tagline: null,
    description: "Esta solução da TechEnabler combina a plataforma Kentik com os serviços SOCaaS da TechEnabler entregando análise contínua e inteligente de tráfego de rede. Permite Network Capacity Planning, otimização de peering e interconexão, bem como OTT/CDN Analytics por meio de menu abrangente de parâmetros de análise e diagnóstico de redes. O serviço inclui o SOCaaS Services Operations Center TechEnabler para suporte 24x7.",
    primaryMarket: 'operadoras-isps',
    relatedMarkets: ["operadores-servicos-nuvem"],
    technicalCategories: ["Next Generation Observability"],
    image: "/technabler-solucao-managed-traffic-analysis.webp",
    targetAudience: {
      idealClient: "provedores ISPs, operadoras de telecomunicações e empresas digitais que necessitam de análise contínua de tráfego com suporte especializado.",
      useCases: ["análise de tráfego gerenciada, otimização de custos operacionais, suporte a decisões estratégicas, análise de CDN e OTT, planejamento gerenciado de capacidade."]
    },
    benefits: ["serviço completo e gerenciado, SOC dedicado, inteligência operacional, suporte local especializado."],
    competitiveAdvantages: ["integração com múltiplas tecnologias, expertise consolidada."],
    uniqueValue: "redução de custo, otimização de tempo, visibilidade estratégica.",
    keyFeatures: ["análise de fluxo completo L3/L4 com capacity planning, monitoramento sintético para validação de serviços, automação de processos, análise de experiência digital."],
    technicalDetails: ["suporte multiprotocolo, APIs abertas, escalabilidade massiva."],
    commercialModels: ["OPEX: SaaS, as a Service."]
  },
  'mssp-services': {
    title: "TE - MSSP Services",
    slug: 'mssp-services',
    sku: "TECH-MSSP",
    subtitle: null,
    tagline: null,
    description: "Estes serviços combinam a expertise em SOCaaS Services Operations Center com tecnologias avançadas para fornecer proteção DDoS e observabilidade de classe mundial. Integramos world class SOC Services em modo 24x7x365 por meio de parcerias estratégicas com fornecedores de tecnologia, oferecendo proteção e observabilidade multi-tenant.",
    primaryMarket: 'operadoras-isps',
    relatedMarkets: ["operadores-servicos-nuvem","redes-corporativas"],
    technicalCategories: ["Network Defense"],
    image: "/technabler-solucao-mssp-services.webp",
    targetAudience: {
      idealClient: "ISPs e empresas digitais que necessitem de acompanhamento intensivo de seus mecanismos de proteção.",
      useCases: ["proteção Always-On, managed services provider, multi-tenant portal."]
    },
    benefits: ["proteção sem downtime, monitoramento e suporte constante, escalabilidade, visibilidade completa."],
    competitiveAdvantages: ["nossa equipe possui certificação MSSP disponibilizando o mais alto grau de conhecimento e qualidade na prestação de serviços."],
    uniqueValue: "redução no MTTR e otimização do TCO.",
    keyFeatures: ["SecureWatch Analytics, portal multi-tenant, automated provisioning."],
    technicalDetails: ["altíssima capacidade disponível (dezenas de terabits por segundo)."],
    commercialModels: ["OPEX: SaaS, as a Service."]
  },
  'no-appliance-detection': {
    title: "TE - No Appliance Detection",
    slug: 'no-appliance-detection',
    sku: "NA-TECH-DETECTOR",
    subtitle: "Proteja sua rede sem necessidade de investimento",
    tagline: null,
    description: "A solução No Appliance Detection da TechEnabler oferece serviço de monitoramento contínuo do tráfego de rede por meio da análise de flows gerados a partir dos roteadores da rede do cliente. Assim, identifica e classifica automaticamente ataques DDoS em tempo real, permitindo ao cliente tomar as ações necessárias para evitar prejuízos à sua rede. A solução é implementada como serviço, incluindo consultoria, projeto e implementação da TechEnabler, sem necessidade de investimento em Capex por parte do cliente.",
    primaryMarket: 'operadoras-isps',
    relatedMarkets: ["redes-corporativas"],
    technicalCategories: ["Network Defense"],
    image: "/technabler-solucao-no-appliance-detection.webp",
    targetAudience: {
      idealClient: "provedores de serviços de todos os portes, e médias e grandes empresas que necessitam proteger seus links e aplicações críticas contra ataques DDoS, sem investimento em estrutura dedicada.",
      useCases: ["detecção de ataques DDoS, monitoramento de tráfego anômalo, geração de relatórios de ameaças e apoio à equipe de Security Operation Center (SOC)."]
    },
    benefits: ["não introduz latência perceptível, proteção Always-On com detecção em segundos, dashboards em tempo real e alertas proativos."],
    competitiveAdvantages: ["plataforma escalável, flexível, retentividade de dados brutos por longos períodos, integração multi-tenant, APIs para automação e orquestração, relatórios detalhados."],
    uniqueValue: "economia de tempo no diagnóstico, antecipação de falhas, geração de receitas via portal multi-tenant, incremento da segurança sem necessidade de assets físicos.",
    keyFeatures: ["compatível com NetFlow, sFlow, IPFIX, SNMP, e BGP; incluindo monitoramento por amostragem e coleta de dados em ambientes físicos, virtuais e nuvem."],
    technicalDetails: ["serviço baseado em plataforma Kentik e SOC TechEnabler."],
    commercialModels: ["OPEX: SaaS, as a Service."]
  },
  'no-appliance-protection': {
    title: "TE - No Appliance Protection",
    slug: 'no-appliance-protection',
    sku: "NA-TECH-PROTECTOR",
    subtitle: "Transforme seus roteadores em sua primeira linha de defesa",
    tagline: null,
    description: "A solução No Appliance Protection da TechEnabler oferece serviço de monitoramento e mitigação contínuo do tráfego de rede por meio da análise de flows gerados a partir dos roteadores da rede do cliente. Identifica, classifica e mitiga automaticamente ataques DDoS em tempo real, por meio da exportação de políticas diretamente aos roteadores do cliente. A solução é implementada como serviço, incluindo consultoria, projeto e implementação TechEnabler, sem necessidade de investimento em Capex por parte do cliente.",
    primaryMarket: 'operadoras-isps',
    relatedMarkets: ["redes-corporativas"],
    technicalCategories: ["Network Defense"],
    image: "/technabler-solucao-no-appliance-protection.webp",
    targetAudience: {
      idealClient: "provedores de serviços de todos os portes, médias e grandes empresas que necessitam proteger seus links e aplicações críticas contra ataques DDoS por meio de ações de mitigação automáticas, sem investimento em estrutura dedicada.",
      useCases: ["detecção e combate automático a ataques DDoS, monitoramento de tráfego anômalo, geração de relatórios de ameaças e apoio à equipe de Security Operation Center (SOC)."]
    },
    benefits: ["não introduz latência perceptível, proteção Always-On com detecção e combate em segundos, dashboards em tempo real e alertas proativos."],
    competitiveAdvantages: ["plataforma escalável, flexível, retentividade de dados brutos por longos períodos, integração multi-tenant, APIs para automação e orquestração, relatórios detalhados."],
    uniqueValue: "economia de tempo no diagnóstico e combate, antecipação de falhas, geração de receitas via portal multi-tenant, incremento da segurança sem necessidade de assets físicos.",
    keyFeatures: ["compatível com NetFlow, sFlow, IPFIX, SNMP, e BGP; incluindo monitoramento por amostragem e coleta de dados em ambientes físicos, virtuais e nuvem."],
    technicalDetails: ["serviço baseado em plataforma Kentik e SOC TechEnabler."],
    commercialModels: ["OPEX: SaaS, as a Service."]
  },
  'smart-dns-corporations': {
    title: "TE - Smart DNS for Corporations",
    slug: 'smart-dns-corporations',
    sku: "TECH-SMART-DNS-CORP",
    subtitle: null,
    tagline: null,
    description: "Este serviço desenvolvido pela TechEnabler utiliza a tecnologia de DNS seguro da Planisys e compõe uma solução adequada às necessidades das empresas para proteger o tráfego de saída de sua rede, por meio de PDNS e RPZ (Response Policy Zone).",
    primaryMarket: 'redes-corporativas',
    relatedMarkets: [],
    technicalCategories: ["Network Defense"],
    image: "/technabler-solucao-smart-dns-for-corporations.webp",
    targetAudience: {
      idealClient: "empresas de todo porte que desejem ter uma estratégia de navegação segura, impedindo o acesso a sites maliciosos com processos atualizados por bases de dados de cibersegurança globais.",
      useCases: ["proteção de usuários contra links perigosos; proteção de sistemas de IoT; proteção contra phishing e malware; resolvers RPZ gerenciados pelo cliente; política de bloqueio; black list de DNS; atendimento a bloqueios da ANATEL (nativamente)."]
    },
    benefits: ["serviço transparente para o usuário; configuração sem interrupção de serviços; software permanentemente atualizado; sistema de gestão centralizado; instalação dedicada por perfil de proteção."],
    competitiveAdvantages: ["não é necessária a instalação de aplicativos; sistema de gestão simples via web; gestão de alto nível suportada pela TechEnabler com reports e analytics."],
    uniqueValue: "operação simples sem necessidade de especialização da equipe; venda de solução de segurança a todos os usuários da rede da empresa.",
    keyFeatures: ["PDNS/RPZ com threat intelligence."],
    technicalDetails: ["o software RPZ é instalado juntamente com o DNS. É instalado um RPZ para cada perfil de proteção definido pelo cliente. Instalação em nuvem sem impacto na rede do cliente."],
    commercialModels: ["OPEX: SaaS, as a Service."]
  },
  'smart-dns-isps': {
    title: "TE - Smart DNS for ISPs",
    slug: 'smart-dns-isps',
    sku: "TECH-SMART-DNS-ISP",
    subtitle: null,
    tagline: null,
    description: "Este serviço desenvolvido pela TechEnabler utiliza a Tecnologia de DNS seguro da Planisys e compõe uma solução adequada às necessidades dos ISPs para proteger o tráfego de saída tanto de sua rede quanto da rede de seus clientes por meio de PDNS e RPZ (Response Policy Zone).",
    primaryMarket: 'operadoras-isps',
    relatedMarkets: ["operadores-servicos-nuvem"],
    technicalCategories: ["Network Defense"],
    targetAudience: {
      idealClient: "ISPs e operadoras de telecomunicações.",
      useCases: ["proteção de usuários contra links perigosos; proteção de sistemas de IoT; proteção contra phishing e malware; resolvers RPZ gerenciados pelo ISP; política de bloqueio; black list de DNS; atendimento a bloqueios da ANATEL (nativamente); revenda do serviço de proteção para clientes finais do ISP."]
    },
    benefits: ["serviço transparente para o usuário; configuração sem interrupção de serviços; software permanentemente atualizado; sistema de gestão centralizado customizável."],
    competitiveAdvantages: ["sistema multi-tenant; cada instalação de DNS tem um RPZ próprio; configuração mínima na rede existente; apenas apontamento do roteador para o endereço do RPZ."],
    uniqueValue: "operação simples sem necessidade de especialização da equipe; venda de solução de segurança aos clientes finais do ISP (solução monetizável).",
    keyFeatures: ["PDNS/RPZ com threat intelligence."],
    technicalDetails: ["o software RPZ é instalado juntamente com o DNS. É instalado um RPZ para cada perfil de proteção definido pelo cliente. Instalação on-premises, em nuvem ou híbrida."],
    commercialModels: ["OPEX: SaaS, as a Service."],
    image: "/technabler-solucao-smart-dns-isps-arquitetura.webp"
  },
  'soc-services': {
    title: "TE - SOC Services",
    slug: 'soc-services',
    sku: "TECH-SOC",
    subtitle: null,
    tagline: null,
    description: "A TechEnabler desenvolveu um novo modelo de negócio em Service Operations Center, com o objetivo de oferecer visibilidade e proteção proativa por meio de diversas tecnologias integradas. Somos pioneiros no modelo SOC as a Service, oferecendo observabilidade completa da rede por meio de técnicas de AIOPS.",
    primaryMarket: 'operadoras-isps',
    relatedMarkets: ["operadores-servicos-nuvem","redes-corporativas"],
    technicalCategories: ["Network Defense"],
    image: "/technabler-solucao-soc-services.webp",
    targetAudience: {
      idealClient: "ISPs, operadoras e empresas digitais que necessitem terceirizar o trabalho do SOC, ou ter uma equipe de alto nível dando suporte a sua equipe local.",
      useCases: ["monitoração proativa 24x7, detecção de ataques DDoS, análise comportamental de tráfego."]
    },
    benefits: ["proteção sem downtime, Always-On, com suporte local em português."],
    competitiveAdvantages: ["World-Class SOC Service 24x7x365 support."],
    uniqueValue: "capacidade e implementação de Portal de Monitoração de Incidentes para infraestrutura do cliente e para clientes finais gerando receita no modelo multi-tenant.",
    keyFeatures: ["sistema analítico com IA e portal multi-tenant."],
    technicalDetails: ["serviço prestado por meio da combinação de tecnologias Kentik, Corero, Unryo e SOC TechEnabler."],
    commercialModels: ["OPEX: SaaS, as a Service."]
  },
  'cloud-defense': {
    title: "TE - Cloud Defense",
    slug: 'cloud-defense',
    sku: "TECHCLOUD",
    subtitle: "O serviço anti-DDoS em nuvem da TechEnabler",
    tagline: null,
    description: "O serviço TechCloud é o Cloud Scrubbing da TechEnabler. Trata-se de uma nuvem de mitigação de ataque DDoS com qualidade premium em todos os seus elementos, desde os provedores de trânsito até os equipamentos de roteamento e sistema de mitigação. Esse scrubbing pode ser conectado por meio do ASN 274830, e, com parcerias locais podemos fazer entregas em praticamente todo o território latino-americano.",
    primaryMarket: 'operadoras-isps',
    relatedMarkets: ["operadores-servicos-nuvem","redes-corporativas"],
    technicalCategories: ["Network Defense"],
    image: "/technabler-solucao-cloud-defense.webp",
    targetAudience: {
      idealClient: "empresas de qualquer tamanho que desejem contratar uma proteção em nuvem.",
      useCases: ["bloquear ataques DDoS, sem impactar a infraestrutura própria."]
    },
    benefits: ["maior poder e eficiência de mitigação, portal de análise real-time, acesso a ampla rede de transporte, defesa da camada três até a camada sete da rede."],
    competitiveAdvantages: ["podemos oferecer detector para automatizar o processo de anúncio para a solução."],
    uniqueValue: "maior eficiência de mitigação e suporte premium TechEnabler 24x7.",
    keyFeatures: ["Proteção Always-On; Proteção On-Demand."],
    technicalDetails: ["Incluir o AS ou AS-Cone no RADb, com liberação em poucos minutos; determinar a forma de entrega do tráfego limpo: PNI, IX ou GRE."],
    commercialModels: ["Serviço de Proteção."]
  }
};

export interface MarketData {
  key: MarketKey;
  title: string;
  slug: string;
  description: string; // Short description
  fullDescription: string; // Detailed description for home/landing pages
  challenges: string[];
}

export const marketsData: MarketData[] = [
  {
    key: 'operadoras-isps' as MarketKey,
    title: 'Para operadoras e ISPs',
    slug: '/mercados/operadoras-isps',
    description: 'Segurança, observabilidade e performance para redes que sustentam a conectividade.',
    fullDescription: 'Para operadoras e provedores de serviços de internet, a disponibilidade e a segurança da rede são o próprio negócio. A TechEnabler oferece soluções que protegem a infraestrutura contra ataques, automatizam a mitigação de problemas e ampliam a visibilidade operacional.',
    challenges: [
      'Proteção contra ataques DDoS massivos',
      'Otimização de peering e trânsito IP',
      'Monetização de serviços de segurança (SVA)',
      'Observabilidade de backbone global'
    ]
  },
  {
    key: 'operadores-servicos-nuvem' as MarketKey,
    title: 'Para operadores de serviços em nuvem',
    slug: '/mercados/operadores-servicos-nuvem',
    description: 'Proteção e analytics para ambientes digitais e infraestruturas distribuídas.',
    fullDescription: 'Operadores de nuvem e data centers exigem resiliência absoluta e leitura precisa sobre o uso de seus recursos. Nossas soluções de defesa em nuvem, analytics avançado e observabilidade full-stack garantem que o ambiente digital esteja sempre protegido.',
    challenges: [
      'Visibilidade unificada multi-cloud',
      'Performance de aplicações OTT/CDN',
      'Segurança de infraestrutura escalável',
      'Monitoramento de experiência digital'
    ]
  },
  {
    key: 'redes-corporativas' as MarketKey,
    title: 'Para redes corporativas',
    slug: '/mercados/redes-corporativas',
    description: 'Confiabilidade, segurança e operação especializada para a TI de grandes organizações.',
    fullDescription: 'Empresas de diferentes setores dependem de redes corporativas robustas para sustentar sua operação. A TechEnabler ajuda a TI corporativa a implementar camadas de defesa baseadas em DNS, integrar tecnologias de rede e prever falhas com AIOps.',
    challenges: [
      'Segurança de tráfego de saída (DNS)',
      'Proteção de ativos críticos sem hardware Capex',
      'Observabilidade de redes híbridas',
      'Monitoramento de dispositivos IoT'
    ]
  },
  {
    key: 'ti-corporativa' as MarketKey,
    title: 'Para TI corporativa',
    slug: '/mercados/ti-corporativa',
    description: 'Eficiência operacional e visibilidade total para times de TI e Segurança.',
    fullDescription: 'Times de TI e segurança precisam de ferramentas que automatizem o diagnóstico e forneçam visibilidade clara sobre o desempenho de aplicações e infraestrutura. A TechEnabler entrega observabilidade avançada e serviços profissionais para otimizar a operação corporativa.',
    challenges: [
      'Redução de MTTR em incidentes',
      'Compliance e auditoria de tráfego',
      'Simplificação da gestão de ferramentas',
      'Detecção proativa de anomalias'
    ]
  }
];
